import { useCallback, useRef, type ComponentRef } from 'react';
import type { TextInput } from 'react-native';
import { useSafeTimeout } from './useSafeTimeout';
import type { ScrollIntoViewOnFocus } from './useScrollIntoViewOnFocus';

/**
 * 키보드가 올라오며 뷰포트가 줄어드는 동안 스크롤하면 다시 접힌 아래로 들어간다.
 * 줄어든 뒤(=레이아웃이 확정된 뒤)에 위치를 잡기 위한 지연.
 */
const KEYBOARD_SETTLE_MS = 300;

/**
 * 포커스한 입력창을 보이는 영역으로 스크롤한다(웹).
 *
 * 웹은 `KeyboardAvoiding` 이 no-op 이고(`KeyboardAvoiding.web.tsx`) 바텀시트가
 * `position: fixed` 오버레이 안에 있어, 브라우저의 자동 scroll-into-view 가 시트 내부
 * 스크롤 컨테이너까지 닿지 않는다. 그래서 포커스 시점에 직접 스크롤한다.
 *
 * 전제: 인풋 글자 크기가 16px 이상이어야 한다. 그보다 작으면 iOS Safari 가 포커스 시 페이지를
 * 확대하고 좌표계가 밀려서, 스크롤을 해도 엉뚱한 곳으로 간다.
 *
 * RNW 의 TextInput ref 는 실제 DOM 노드라 `scrollIntoView` 를 그대로 호출할 수 있다.
 */
export const useScrollIntoViewOnFocus = (): ScrollIntoViewOnFocus => {
  const ref = useRef<ComponentRef<typeof TextInput> | null>(null);
  const setSafeTimeout = useSafeTimeout();

  const onFocus = useCallback(() => {
    setSafeTimeout(() => {
      const node = ref.current as unknown as HTMLElement | null;
      node?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    }, KEYBOARD_SETTLE_MS);
  }, [setSafeTimeout]);

  return { ref, onFocus };
};
